/**
 * Tenant Usage Dashboard Page
 *
 * Self-service usage dashboard for tenant admins.
 * Phase 3.6.3: Usage Dashboard for Tenants
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  ActivityIcon,
  UsersIcon,
  DatabaseIcon,
  ZapIcon,
  RefreshIcon,
  AlertIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CpuIcon,
  ServerIcon,
} from '../components/common/Icons';
import { useAuth } from '../contexts/AuthContext';

// ============================================================================
// Types (match backend schemas from tenant_portal/usage.py)
// ============================================================================

interface UsageMetric {
  current: number;
  limit: number | null;
  percentage: number | null;
}

interface CurrentUsageResponse {
  tenant_id: string;
  updated_at: string | null;
  api_requests_today: UsageMetric;
  api_requests_month: number;
  tokens_today: number;
  tokens_month: UsageMetric;
  llm_requests_today: number;
  llm_requests_month: number;
  storage: UsageMetric;
  storage_db_bytes: number;
  storage_files_bytes: number;
  total_users: number;
  active_users_today: number;
  active_users_month: number;
}

interface UsageSummaryResponse {
  tenant_id: string;
  period: string;
  api_requests: number;
  api_requests_change_percent: number | null;
  tokens_used: number;
  tokens_change_percent: number | null;
  active_users: number;
  active_users_change_percent: number | null;
  storage_bytes: number;
  storage_change_percent: number | null;
  api_limit_status: 'ok' | 'warning' | 'exceeded';
  token_limit_status: 'ok' | 'warning' | 'exceeded';
  storage_limit_status: 'ok' | 'warning' | 'exceeded';
}

interface EndpointUsage {
  endpoint: string;
  request_count: number;
  avg_latency_ms: number;
  error_count: number;
  error_rate: number;
}

interface TopEndpointsResponse {
  tenant_id: string;
  start_date: string;
  end_date: string;
  endpoints: EndpointUsage[];
}

interface ModelUsage {
  model: string;
  requests: number;
  tokens_in: number;
  tokens_out: number;
  tokens_total: number;
  percentage_of_total: number;
}

interface ModelUsageResponse {
  tenant_id: string;
  start_date: string;
  end_date: string;
  models: ModelUsage[];
  total_requests: number;
  total_tokens: number;
}

// ============================================================================
// Helper Functions
// ============================================================================

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function formatNumber(num: number): string {
  if (num >= 1_000_000_000) return (num / 1_000_000_000).toFixed(1) + 'B';
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + 'M';
  if (num >= 1_000) return (num / 1_000).toFixed(1) + 'K';
  return num.toString();
}

function getStatusColor(status: 'ok' | 'warning' | 'exceeded'): string {
  switch (status) {
    case 'ok':
      return 'var(--success)';
    case 'warning':
      return 'var(--warning)';
    case 'exceeded':
      return 'var(--error)';
    default:
      return 'var(--text-secondary)';
  }
}

function getChangeIcon(change: number | null) {
  if (change === null) return null;
  return change >= 0 ? (
    <ArrowUpIcon size={14} style={{ color: change > 10 ? 'var(--warning)' : 'var(--success)' }} />
  ) : (
    <ArrowDownIcon size={14} style={{ color: 'var(--success)' }} />
  );
}

// ============================================================================
// Components
// ============================================================================

interface StatCardProps {
  label: string;
  value: string;
  subValue?: string;
  change?: number | null;
  icon: React.ReactNode;
  status?: 'ok' | 'warning' | 'exceeded';
}

const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  subValue,
  change,
  icon,
  status = 'ok',
}) => (
  <div
    className="stat-card"
    style={{
      background: 'rgba(255, 255, 255, 0.03)',
      borderRadius: '12px',
      padding: '20px',
      border: `1px solid ${status === 'exceeded' ? 'rgba(239, 68, 68, 0.3)' : status === 'warning' ? 'rgba(245, 158, 11, 0.3)' : 'rgba(255, 255, 255, 0.08)'}`,
      display: 'flex',
      alignItems: 'flex-start',
      gap: '16px',
    }}
  >
    <div
      style={{
        background: `${getStatusColor(status)}20`,
        borderRadius: '10px',
        padding: '12px',
        color: getStatusColor(status),
      }}
    >
      {icon}
    </div>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
        {label}
      </div>
      <div style={{ fontSize: '24px', fontWeight: 600, color: 'var(--text-primary)' }}>
        {value}
      </div>
      {(subValue || change !== undefined) && (
        <div
          style={{
            fontSize: '12px',
            color: 'var(--text-secondary)',
            marginTop: '4px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          {change !== null && change !== undefined && (
            <>
              {getChangeIcon(change)}
              <span>{Math.abs(change).toFixed(1)}% vs last month</span>
            </>
          )}
          {subValue && !change && <span>{subValue}</span>}
        </div>
      )}
    </div>
  </div>
);

interface UsageBarProps {
  label: string;
  current: number;
  limit: number | null;
  percentage: number | null;
  formatValue?: (v: number) => string;
}

const UsageBar: React.FC<UsageBarProps> = ({
  label,
  current,
  limit,
  percentage,
  formatValue = formatNumber,
}) => {
  const pct = percentage ?? 0;
  const barColor =
    pct >= 100
      ? 'var(--error)'
      : pct >= 80
        ? 'var(--warning)'
        : 'var(--primary)';

  return (
    <div style={{ marginBottom: '16px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '8px',
          fontSize: '13px',
        }}
      >
        <span style={{ color: 'var(--text-primary)' }}>{label}</span>
        <span style={{ color: 'var(--text-secondary)' }}>
          {formatValue(current)}
          {limit && ` / ${formatValue(limit)}`}
          {percentage !== null && ` (${percentage.toFixed(1)}%)`}
        </span>
      </div>
      <div
        style={{
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '4px',
          height: '8px',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${Math.min(pct, 100)}%`,
            height: '100%',
            background: barColor,
            borderRadius: '4px',
            transition: 'width 0.3s ease',
          }}
        />
      </div>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

const TenantUsageDashboardPage: React.FC = () => {
  const { authFetch } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUsage, setCurrentUsage] = useState<CurrentUsageResponse | null>(null);
  const [summary, setSummary] = useState<UsageSummaryResponse | null>(null);
  const [endpoints, setEndpoints] = useState<TopEndpointsResponse | null>(null);
  const [models, setModels] = useState<ModelUsageResponse | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [usageRes, summaryRes, endpointsRes, modelsRes] = await Promise.all([
        authFetch('/api/tenant-portal/usage'),
        authFetch('/api/tenant-portal/usage/summary'),
        authFetch('/api/tenant-portal/usage/endpoints?days=7&limit=5'),
        authFetch('/api/tenant-portal/usage/models?days=30'),
      ]);

      if (!usageRes.ok || !summaryRes.ok || !endpointsRes.ok || !modelsRes.ok) {
        throw new Error('Failed to fetch usage data');
      }

      setCurrentUsage(await usageRes.json());
      setSummary(await summaryRes.json());
      setEndpoints(await endpointsRes.json());
      setModels(await modelsRes.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load usage data');
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <RefreshIcon size={24} className="spin" />
        <p style={{ marginTop: '16px', color: 'var(--text-secondary)' }}>Loading usage data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          padding: '40px',
          textAlign: 'center',
          background: 'rgba(239, 68, 68, 0.1)',
          borderRadius: '12px',
          margin: '24px',
        }}
      >
        <AlertIcon size={32} style={{ color: 'var(--error)' }} />
        <p style={{ marginTop: '16px', color: 'var(--error)' }}>{error}</p>
        <button
          onClick={fetchData}
          style={{
            marginTop: '16px',
            padding: '8px 16px',
            background: 'var(--primary)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
        }}
      >
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 600, margin: 0 }}>Usage Dashboard</h1>
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginTop: '4px' }}>
            Monitor your API usage, token consumption, and resource utilization
          </p>
        </div>
        <button
          onClick={fetchData}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '8px',
            color: 'var(--text-primary)',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          <RefreshIcon size={16} />
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '16px',
            marginBottom: '24px',
          }}
        >
          <StatCard
            label="API Requests"
            value={formatNumber(summary.api_requests)}
            change={summary.api_requests_change_percent}
            icon={<ActivityIcon size={24} />}
            status={summary.api_limit_status}
          />
          <StatCard
            label="Tokens Used"
            value={formatNumber(summary.tokens_used)}
            change={summary.tokens_change_percent}
            icon={<ZapIcon size={24} />}
            status={summary.token_limit_status}
          />
          <StatCard
            label="Active Users"
            value={summary.active_users.toString()}
            change={summary.active_users_change_percent}
            icon={<UsersIcon size={24} />}
          />
          <StatCard
            label="Storage Used"
            value={formatBytes(summary.storage_bytes)}
            change={summary.storage_change_percent}
            icon={<DatabaseIcon size={24} />}
            status={summary.storage_limit_status}
          />
        </div>
      )}

      {/* Usage Limits Section */}
      {currentUsage && (
        <div
          style={{
            background: 'rgba(255, 255, 255, 0.03)',
            borderRadius: '12px',
            padding: '24px',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            marginBottom: '24px',
          }}
        >
          <h2 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '20px' }}>
            Resource Limits
          </h2>
          <UsageBar
            label="API Requests (Today)"
            current={currentUsage.api_requests_today.current}
            limit={currentUsage.api_requests_today.limit}
            percentage={currentUsage.api_requests_today.percentage}
          />
          <UsageBar
            label="Tokens (This Month)"
            current={currentUsage.tokens_month.current}
            limit={currentUsage.tokens_month.limit}
            percentage={currentUsage.tokens_month.percentage}
          />
          <UsageBar
            label="Storage"
            current={currentUsage.storage.current}
            limit={currentUsage.storage.limit}
            percentage={currentUsage.storage.percentage}
            formatValue={formatBytes}
          />
        </div>
      )}

      {/* Two Column Layout for Tables */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: '24px',
        }}
      >
        {/* Top Endpoints */}
        {endpoints && endpoints.endpoints.length > 0 && (
          <div
            style={{
              background: 'rgba(255, 255, 255, 0.03)',
              borderRadius: '12px',
              padding: '24px',
              border: '1px solid rgba(255, 255, 255, 0.08)',
            }}
          >
            <h2 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
              <ServerIcon size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
              Top API Endpoints (7 days)
            </h2>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '8px 0',
                      fontSize: '12px',
                      color: 'var(--text-secondary)',
                    }}
                  >
                    Endpoint
                  </th>
                  <th
                    style={{
                      textAlign: 'right',
                      padding: '8px 0',
                      fontSize: '12px',
                      color: 'var(--text-secondary)',
                    }}
                  >
                    Requests
                  </th>
                  <th
                    style={{
                      textAlign: 'right',
                      padding: '8px 0',
                      fontSize: '12px',
                      color: 'var(--text-secondary)',
                    }}
                  >
                    Latency
                  </th>
                  <th
                    style={{
                      textAlign: 'right',
                      padding: '8px 0',
                      fontSize: '12px',
                      color: 'var(--text-secondary)',
                    }}
                  >
                    Errors
                  </th>
                </tr>
              </thead>
              <tbody>
                {endpoints.endpoints.map((ep) => (
                  <tr
                    key={ep.endpoint}
                    style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}
                  >
                    <td
                      style={{
                        padding: '12px 0',
                        fontSize: '13px',
                        fontFamily: 'monospace',
                        color: 'var(--primary)',
                      }}
                    >
                      {ep.endpoint}
                    </td>
                    <td
                      style={{
                        textAlign: 'right',
                        padding: '12px 0',
                        fontSize: '13px',
                      }}
                    >
                      {formatNumber(ep.request_count)}
                    </td>
                    <td
                      style={{
                        textAlign: 'right',
                        padding: '12px 0',
                        fontSize: '13px',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      {ep.avg_latency_ms}ms
                    </td>
                    <td
                      style={{
                        textAlign: 'right',
                        padding: '12px 0',
                        fontSize: '13px',
                        color: ep.error_rate > 1 ? 'var(--error)' : 'var(--text-secondary)',
                      }}
                    >
                      {ep.error_count} ({ep.error_rate.toFixed(1)}%)
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Model Usage */}
        {models && models.models.length > 0 && (
          <div
            style={{
              background: 'rgba(255, 255, 255, 0.03)',
              borderRadius: '12px',
              padding: '24px',
              border: '1px solid rgba(255, 255, 255, 0.08)',
            }}
          >
            <h2 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
              <CpuIcon size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
              Model Usage (30 days)
            </h2>
            <div style={{ marginBottom: '16px' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '13px',
                  color: 'var(--text-secondary)',
                }}
              >
                <span>Total: {formatNumber(models.total_tokens)} tokens</span>
                <span>{formatNumber(models.total_requests)} requests</span>
              </div>
            </div>
            {models.models.map((model) => (
              <div key={model.model} style={{ marginBottom: '16px' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '8px',
                    fontSize: '13px',
                  }}
                >
                  <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                    {model.model}
                  </span>
                  <span style={{ color: 'var(--text-secondary)' }}>
                    {formatNumber(model.tokens_total)} tokens ({model.percentage_of_total}%)
                  </span>
                </div>
                <div
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '4px',
                    height: '8px',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${model.percentage_of_total}%`,
                      height: '100%',
                      background: 'var(--primary)',
                      borderRadius: '4px',
                    }}
                  />
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: '4px',
                    fontSize: '11px',
                    color: 'var(--text-secondary)',
                  }}
                >
                  <span>In: {formatNumber(model.tokens_in)}</span>
                  <span>Out: {formatNumber(model.tokens_out)}</span>
                  <span>{model.requests} requests</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Stats Footer */}
      {currentUsage && (
        <div
          style={{
            marginTop: '24px',
            padding: '16px 24px',
            background: 'rgba(255, 255, 255, 0.02)',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '16px',
          }}
        >
          <div style={{ fontSize: '13px' }}>
            <span style={{ color: 'var(--text-secondary)' }}>LLM Requests Today: </span>
            <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
              {currentUsage.llm_requests_today}
            </span>
          </div>
          <div style={{ fontSize: '13px' }}>
            <span style={{ color: 'var(--text-secondary)' }}>DB Storage: </span>
            <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
              {formatBytes(currentUsage.storage_db_bytes)}
            </span>
          </div>
          <div style={{ fontSize: '13px' }}>
            <span style={{ color: 'var(--text-secondary)' }}>File Storage: </span>
            <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
              {formatBytes(currentUsage.storage_files_bytes)}
            </span>
          </div>
          <div style={{ fontSize: '13px' }}>
            <span style={{ color: 'var(--text-secondary)' }}>Total Users: </span>
            <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
              {currentUsage.total_users}
            </span>
          </div>
          {currentUsage.updated_at && (
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              Last updated: {new Date(currentUsage.updated_at).toLocaleString()}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TenantUsageDashboardPage;
